import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Plus,
  Trash,
  FloppyDisk,
  Question,
  Warning
} from '@phosphor-icons/react';

const VOICE_OPTIONS = [
  { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel (Professional Female)' },
  { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni (Professional Male)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah (Friendly Female)' },
  { id: 'VR6AewLTigWG4xSOukaG', name: 'Arnold (Friendly Male)' },
];

const ANSWER_TYPES = ['text', 'yes-no', 'number', 'choice'];

export default function ScriptEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    systemPrompt: '',
    voiceId: '21m00Tcm4TlvDq8ikWAM',
    greeting: '',
    qualifyingQuestions: [],
    closingScript: '',
    objectionHandlers: [],
    maxCallDurationMinutes: 10,
    isActive: true
  });

  useEffect(() => {
    if (!isNew) {
      loadScript();
    }
  }, [id, isNew]);

  const loadScript = async () => {
    try {
      const response = await api.get(`/scripts/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load script');
      navigate('/dashboard/scripts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.systemPrompt) {
      toast.error('Name and system prompt are required');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await api.post('/scripts', formData);
        toast.success('Script created');
      } else {
        await api.put(`/scripts/${id}`, formData);
        toast.success('Script updated');
      }
      navigate('/dashboard/scripts');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      qualifyingQuestions: [
        ...formData.qualifyingQuestions,
        { text: '', expectedType: 'text', required: true }
      ]
    });
  };

  const updateQuestion = (index, field, value) => {
    const questions = [...formData.qualifyingQuestions];
    questions[index] = { ...questions[index], [field]: value };
    setFormData({ ...formData, qualifyingQuestions: questions });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      qualifyingQuestions: formData.qualifyingQuestions.filter((_, i) => i !== index)
    });
  };

  const addObjectionHandler = () => {
    setFormData({
      ...formData,
      objectionHandlers: [
        ...formData.objectionHandlers,
        { trigger: '', response: '' }
      ]
    });
  };

  const updateObjectionHandler = (index, field, value) => {
    const handlers = [...formData.objectionHandlers];
    handlers[index] = { ...handlers[index], [field]: value };
    setFormData({ ...formData, objectionHandlers: handlers });
  };

  const removeObjectionHandler = (index) => {
    setFormData({
      ...formData,
      objectionHandlers: formData.objectionHandlers.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading script...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="script-editor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/scripts"
            className="w-10 h-10 flex items-center justify-center rounded-sm bg-zinc-800 hover:bg-zinc-700 transition-colors"
            data-testid="back-to-scripts"
          >
            <ArrowLeft size={20} className="text-zinc-400" />
          </Link>
          <h1 className="text-2xl font-medium text-zinc-50 tracking-tight">
            {isNew ? 'Create Script' : 'Edit Script'}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-zinc-400">Active</Label>
            <Switch
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              data-testid="script-active-toggle"
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 flex items-center gap-2"
            data-testid="save-script-btn"
          >
            <FloppyDisk size={18} /> {saving ? 'Saving...' : 'Save Script'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl">
        {/* Basic Info */}
        <div className="card">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Script Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., SaaS Demo Booking"
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="script-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what this script is for"
                className="bg-zinc-800 border-zinc-700"
                rows={2}
                data-testid="script-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Voice</Label>
                <Select value={formData.voiceId} onValueChange={(v) => setFormData({ ...formData, voiceId: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="script-voice-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOICE_OPTIONS.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Max Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.maxCallDurationMinutes}
                  onChange={(e) => setFormData({ ...formData, maxCallDurationMinutes: parseInt(e.target.value) })}
                  min={1}
                  max={60}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="script-duration-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* System Prompt */}
        <div className="card">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">AI System Prompt *</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Define the AI agent's personality, goals, and behavior. This is the core instruction for the conversation.
          </p>
          <Textarea
            value={formData.systemPrompt}
            onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
            placeholder="You are a sales representative for [Company]. Your goal is to qualify leads by understanding their needs, budget, and timeline. Be professional, friendly, and conversational..."
            className="bg-zinc-800 border-zinc-700 font-mono text-sm"
            rows={8}
            required
            data-testid="script-systemprompt-input"
          />
        </div>

        {/* Greeting */}
        <div className="card">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Opening Greeting</h3>
          <p className="text-sm text-zinc-400 mb-4">
            The first thing the AI says when the call connects. Use {'{firstName}'} to personalize.
          </p>
          <Textarea
            value={formData.greeting || ''}
            onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
            placeholder="Hi {firstName}, this is Sarah from Acme Corp. How are you doing today?"
            className="bg-zinc-800 border-zinc-700"
            rows={3}
            data-testid="script-greeting-input"
          />
        </div>

        {/* Qualifying Questions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-zinc-50">Qualifying Questions</h3>
              <p className="text-sm text-zinc-400">Questions the AI will ask to qualify the lead</p>
            </div>
            <Button
              type="button"
              onClick={addQuestion}
              variant="outline"
              className="border-zinc-700 flex items-center gap-1"
              data-testid="add-question-btn"
            >
              <Plus size={16} /> Add Question
            </Button>
          </div>

          {formData.qualifyingQuestions.length > 0 ? (
            <div className="space-y-4">
              {formData.qualifyingQuestions.map((q, index) => (
                <div key={index} className="bg-zinc-900 rounded-sm p-4 border border-zinc-800" data-testid={`question-${index}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label className="text-zinc-500 text-xs">Question {index + 1}</Label>
                        <Input
                          value={q.text}
                          onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                          placeholder="What's your current budget for this project?"
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label className="text-zinc-500 text-xs">Expected Answer Type</Label>
                          <Select value={q.expectedType} onValueChange={(v) => updateQuestion(index, 'expectedType', v)}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ANSWER_TYPES.map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={q.required}
                              onCheckedChange={(checked) => updateQuestion(index, 'required', checked)}
                            />
                            <Label className="text-zinc-400 text-xs">Required</Label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="text-zinc-500 hover:text-red-400 p-1"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Question size={32} className="mx-auto mb-2 opacity-50" />
              <p>No questions added yet</p>
            </div>
          )}
        </div>

        {/* Objection Handlers */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-zinc-50">Objection Handlers</h3>
              <p className="text-sm text-zinc-400">How the AI should respond to common objections</p>
            </div>
            <Button
              type="button"
              onClick={addObjectionHandler}
              variant="outline"
              className="border-zinc-700 flex items-center gap-1"
              data-testid="add-objection-btn"
            >
              <Plus size={16} /> Add Handler
            </Button>
          </div>

          {formData.objectionHandlers.length > 0 ? (
            <div className="space-y-4">
              {formData.objectionHandlers.map((h, index) => (
                <div key={index} className="bg-zinc-900 rounded-sm p-4 border border-zinc-800" data-testid={`objection-${index}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label className="text-zinc-500 text-xs">When they say...</Label>
                        <Input
                          value={h.trigger}
                          onChange={(e) => updateObjectionHandler(index, 'trigger', e.target.value)}
                          placeholder="I'm not interested"
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-xs">Respond with...</Label>
                        <Textarea
                          value={h.response}
                          onChange={(e) => updateObjectionHandler(index, 'response', e.target.value)}
                          placeholder="I completely understand. May I ask what's holding you back? Is it timing, budget, or something else?"
                          className="bg-zinc-800 border-zinc-700"
                          rows={2}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeObjectionHandler(index)}
                      className="text-zinc-500 hover:text-red-400 p-1"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Warning size={32} className="mx-auto mb-2 opacity-50" />
              <p>No objection handlers added yet</p>
            </div>
          )}
        </div>

        {/* Closing Script */}
        <div className="card">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Closing Script</h3>
          <p className="text-sm text-zinc-400 mb-4">
            What the AI says to close the conversation (when successful or scheduling a follow-up)
          </p>
          <Textarea
            value={formData.closingScript || ''}
            onChange={(e) => setFormData({ ...formData, closingScript: e.target.value })}
            placeholder="Thank you so much for your time today, {firstName}. I'll send over the details we discussed. Is there anything else I can help you with?"
            className="bg-zinc-800 border-zinc-700"
            rows={3}
            data-testid="script-closing-input"
          />
        </div>
      </form>
    </div>
  );
}
